/** Small hooks shared by the menu screens: layout mode, input hints, assets and animation. */
import { lookOf, playableFighter, type Look } from '@caged/shared';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { loadManifest, type AssetManifest } from '../assets/manifest';
import { DEV_GUEST, devGuestId } from '../net/config';
import { useApp } from '../state/store';

/** A registered account (or a registered dev player): leagues, and the tourney later. */
export function useRegistered(): boolean {
  const account = useApp((st) => st.account);
  return (!!account?.signedIn && !account.guest) || (DEV_GUEST && devGuestId().startsWith('reg-'));
}

/**
 * desktop: wide and tall enough for the desktop designs.
 * landscape: a phone on its side (the fight loop uses the 844×390 designs).
 * portrait: everything else (phones upright, narrow windows).
 */
export type LayoutMode = 'desktop' | 'landscape' | 'portrait';

const DESKTOP_QUERY = '(min-width: 1024px) and (min-height: 560px)';
const LANDSCAPE_QUERY = '(orientation: landscape) and (max-height: 559px)';

function media(query: string): MediaQueryList | null {
  return typeof matchMedia === 'function' ? matchMedia(query) : null;
}

function subscribeQueries(queries: string[], cb: () => void): () => void {
  const lists = queries.map(media).filter((m): m is MediaQueryList => !!m);
  for (const m of lists) m.addEventListener('change', cb);
  return () => {
    for (const m of lists) m.removeEventListener('change', cb);
  };
}

const layoutSubscribe = (cb: () => void) => subscribeQueries([DESKTOP_QUERY, LANDSCAPE_QUERY], cb);

function layoutSnapshot(): LayoutMode {
  if (media(DESKTOP_QUERY)?.matches) return 'desktop';
  if (media(LANDSCAPE_QUERY)?.matches) return 'landscape';
  return 'portrait';
}

export function useLayoutMode(): LayoutMode {
  return useSyncExternalStore(layoutSubscribe, layoutSnapshot, () => 'portrait');
}

/** A phone on its side with little height left, e.g. under iPhone Safari's address and tab bars. */
const SHORT_QUERY = '(orientation: landscape) and (max-height: 359px)';
const shortSubscribe = (cb: () => void) => subscribeQueries([SHORT_QUERY], cb);
const shortSnapshot = () => !!media(SHORT_QUERY)?.matches;

export function useShortLandscape(): boolean {
  return useSyncExternalStore(shortSubscribe, shortSnapshot, () => false);
}

const FINE_QUERY = '(any-pointer: fine)';
const fineSubscribe = (cb: () => void) => subscribeQueries([FINE_QUERY], cb);
const fineSnapshot = () => !!media(FINE_QUERY)?.matches;

/** A mouse or trackpad is present: show keyboard hints (a touch laptop shows both). */
export function useFinePointer(): boolean {
  return useSyncExternalStore(fineSubscribe, fineSnapshot, () => false);
}

const RM_QUERY = '(prefers-reduced-motion: reduce)';
const rmSubscribe = (cb: () => void) => subscribeQueries([RM_QUERY], cb);
const rmSnapshot = () => !!media(RM_QUERY)?.matches;

/** Reduced motion from the OS or the in-game setting. */
export function useReducedMotion(): boolean {
  const os = useSyncExternalStore(rmSubscribe, rmSnapshot, () => false);
  const setting = useApp((s) => s.settings.reducedMotion);
  return os || setting;
}

let manifestCache: AssetManifest | null = null;

/** The asset manifest (fighters and arenas); null until loaded (then cached for later mounts). */
export function useManifest(): AssetManifest | null {
  const [manifest, setManifest] = useState<AssetManifest | null>(manifestCache);
  useEffect(() => {
    if (manifestCache) return;
    let alive = true;
    loadManifest()
      .then((m) => {
        manifestCache = m;
        if (alive) setManifest(m);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return manifest;
}

/** Date.now(), refreshed every `intervalMs`. */
// One clock for every countdown on screen, ticking on the second, so they all read the same (D50).
let secondNow = Date.now();
let secondTimer: ReturnType<typeof setTimeout> | null = null;
const secondListeners = new Set<() => void>();

function nextSecond(): void {
  secondTimer = setTimeout(() => {
    secondNow = Date.now();
    for (const l of secondListeners) l();
    nextSecond();
  }, 1005 - (Date.now() % 1000));
}

function subscribeSecond(cb: () => void): () => void {
  secondListeners.add(cb);
  if (!secondTimer) {
    secondNow = Date.now();
    nextSecond();
  }
  return () => {
    secondListeners.delete(cb);
    if (!secondListeners.size && secondTimer) {
      clearTimeout(secondTimer);
      secondTimer = null;
    }
  };
}

/** The time, updated as each second starts; shared by every caller. */
export function useSecondNow(): number {
  return useSyncExternalStore(subscribeSecond, () => secondNow, () => secondNow);
}

export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * 0 → 1 over `durationMs` after `delayMs`, eased out (cubic), as the designs' count-ups.
 * Jumps straight to 1 with reduced motion.
 */
export function useEaseIn(delayMs: number, durationMs: number): number {
  const reduced = useReducedMotion();
  const [t, setT] = useState(reduced ? 1 : 0);
  useEffect(() => {
    if (reduced) {
      setT(1);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const raw = Math.min(1, Math.max(0, (now - start - delayMs) / durationMs));
      setT(1 - (1 - raw) ** 3);
      if (raw < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [delayMs, durationMs, reduced]);
  return t;
}

/** Scrolls the page to the top when a menu screen opens. */
export function useScrollTop(): void {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
}

/** Runs `cb` when a key is pressed (ignored while typing in a field). */
export function useKey(key: string, cb: (e: KeyboardEvent) => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== key || e.repeat) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      cb(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, cb, enabled]);
}

/**
 * The fighter the player fights as: the one picked in Profile, or the default one while a paid
 * fighter they picked isn't theirs (another account on this device, D52).
 */
export function useMyFighter(): string {
  const progress = useApp((st) => st.progress);
  const picked = useApp((st) => st.settings.fighterId);
  return playableFighter(progress, picked);
}

/** The player's own Locker look (M7): what they wear in the menus and fights. */
export function useMyLook(): Look {
  const progress = useApp((st) => st.progress);
  return useMemo(() => lookOf(progress), [progress]);
}
